import * as React from 'react';
import { Alert, Button, FileDrop, Input, Modal, Panel, Select, Switch } from '@ixicare/ui';
import { Device, DevicePostBody, DeviceType, isImeiNumber, isMacAddress } from '@ixicare/resources';
import { t } from '~i18n';
import { useMutation } from '@tanstack/react-query';
import { getFieldError, humaniseErrors } from '~utils/error';
import { Spacer } from '~components/Utils/Spacer';
import styles from './CreateDeviceModal.module.css';
import { parseDeviceCsv, DeviceCsvRow, requiredCsvColumns } from '~components/Tables/DeviceTables/parseDeviceCsv';
import { buttonOptions } from './CreateInstalledDeviceModal';
import { ScannableInput, ScanResult } from '~components/DeviceScanner';

const CSV_FILE_TYPES = ['text/csv', 'application/vnd.ms-excel', 'application/csv', ''];

const deviceTypeOptions: { label: string; value: DeviceType }[] = [
  { label: t('device.types.ixi_pro'), value: DeviceType.IXI_PRO },
  { label: t('device.types.ixi_lite'), value: DeviceType.IXI_LITE },
  { label: t('device.types.ixi_touch'), value: DeviceType.IXI_TOUCH },
  { label: t('device.types.ixi_black'), value: DeviceType.IXI_BLACK },
  { label: t('device.types.wall_mounted_distress_button'), value: DeviceType.WALL_MOUNTED_DISTRESS_BUTTON },
  { label: t('device.types.eview'), value: DeviceType.EVIEW },
  { label: t('device.types.twig'), value: DeviceType.TWIG },
];

interface CreateDeviceModalProps {
  onClose: () => void;
  onRefresh: () => void;
  siteId: number;
  initialDeviceType: DeviceType;
  isInstaller: boolean;
}

export function CreateDeviceModal({
  onClose,
  onRefresh,
  siteId,
  initialDeviceType,
  isInstaller,
}: CreateDeviceModalProps) {
  const [deviceType, setDeviceType] = React.useState<DeviceType>(initialDeviceType);
  const [mode, setMode] = React.useState<'single' | 'batch'>('single');

  const [macAddress, setMacAddress] = React.useState('');
  const [imeiNumber, setImeiNumber] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [subType, setSubType] = React.useState('REGULAR_BUTTON');

  const [csvFile, setCsvFile] = React.useState<File>();
  const [csvText, setCsvText] = React.useState('');
  const [isFileValid, setIsFileValid] = React.useState(true);

  const showMac = deviceType !== DeviceType.EVIEW && deviceType !== DeviceType.TWIG;
  const showImeiPhone =
    deviceType === DeviceType.IXI_PRO || deviceType === DeviceType.EVIEW || deviceType === DeviceType.TWIG;
  const showSubType = deviceType === DeviceType.WALL_MOUNTED_DISTRESS_BUTTON;

  const parsed = React.useMemo(
    () => (csvText ? parseDeviceCsv(csvText, deviceType) : { rows: [], errors: [] }),
    [csvText, deviceType]
  );

  const buildBody = (row?: DeviceCsvRow): DevicePostBody => ({
    siteId,
    deviceType,
    description: description || undefined,
    ...(showSubType && subType === 'EMERGENCY_BUTTON' ? { subType: 'EMERGENCY_BUTTON' } : {}),
    ...(showMac && { macAddress: (row ? row.macAddress : macAddress)?.trim().toUpperCase() }),
    ...(showImeiPhone && {
      imeiNumber: row ? row.imeiNumber : imeiNumber,
      // Optional, but the API validates any string it gets — an empty one fails parsing.
      phoneNumber: (row ? row.phoneNumber : phoneNumber) || undefined,
    }),
  });

  const createMutation = useMutation({
    // `archive` immediately deletes (= archives) each device right after creation.
    mutationFn: async (archive: boolean) => {
      if (mode === 'single') {
        await Device.submitDevice(buildBody());
        return [] as PromiseSettledResult<unknown>[];
      }
      return Promise.allSettled(
        parsed.rows.map(async (row) => {
          const device = await Device.submitDevice(buildBody(row));
          if (archive) {
            await Device.deleteDevice(Number(device.id));
          }
        })
      );
    },
    onSuccess: (results) => {
      const hasFailure = results.some((r) => r.status === 'rejected');
      if (!hasFailure) {
        onRefresh();
        onClose();
      }
    },
  });

  const handleFile = (file?: File) => {
    setCsvFile(file);
    createMutation.reset();
    if (!file) {
      setCsvText('');
      return;
    }
    file
      .text()
      .then(setCsvText)
      .catch(() => setCsvText(''));
  };

  const onDeviceTypeChange = (value: DeviceType) => {
    setDeviceType(value);
    createMutation.reset();
  };

  // A device QR carries its own type number, so a scan fills the identifier and
  // corrects the type Select. An IMEI barcode carries no type — leave the
  // Select on whichever cellular type the user picked.
  const onScan = (result: ScanResult) => {
    createMutation.reset();
    if (result.type) setDeviceType(result.type);
    if (result.macAddress) setMacAddress(result.macAddress);
    if (result.imeiNumber) setImeiNumber(result.imeiNumber);
  };

  /**
   * Refuses a code this modal can't create, so the camera stays open. Beacons and
   * gateways are placed on a floor plan and have no option here; without this their
   * address would land in the field while the type Select kept its old value,
   * creating the scanned device under the wrong type.
   */
  const validateScan = (result: ScanResult) => {
    if (!result.type || deviceTypeOptions.some((option) => option.value === result.type)) return undefined;
    return t('device.form.scanWrongType', { type: t(`device.types.${result.type.toLowerCase()}`) });
  };

  // Both identifiers live in similar-looking fields on the same label, so a pasted
  // IMEI lands in the MAC field easily. Check the shape, not just that it's filled.
  const macError = macAddress && !isMacAddress(macAddress) ? t('device.form.macInvalid') : undefined;
  const imeiError = imeiNumber && !isImeiNumber(imeiNumber) ? t('device.form.imeiInvalid') : undefined;

  const singleValid = (!showMac || isMacAddress(macAddress)) && (!showImeiPhone || isImeiNumber(imeiNumber));
  const batchValid = !!csvFile && isFileValid && parsed.rows.length > 0 && parsed.errors.length === 0;
  const canSubmit = mode === 'single' ? singleValid : batchValid;

  const settledResults = (createMutation.data ?? []) as PromiseSettledResult<unknown>[];
  const failedResults = settledResults
    .map((result, index) => ({ result, row: parsed.rows[index] }))
    .filter((r): r is { result: PromiseRejectedResult; row: DeviceCsvRow } => r.result.status === 'rejected');
  const succeededCount = settledResults.length - failedResults.length;

  return (
    <Modal className={styles.modal}>
      <>
        <div className={styles.header}>
          <h3 className={styles.title}>
            {t('device.form.create', {
              type: deviceTypeOptions.find((o) => o.value === deviceType)?.label ?? '',
            })}
          </h3>
          <div className={styles.switch}>
            <Switch
              label={mode === 'batch' ? t('device.form.addModeBatch') : t('device.form.addModeSingle')}
              checked={mode === 'batch'}
              onChange={(checked) => {
                setMode(checked ? 'batch' : 'single');
                createMutation.reset();
              }}
            />
          </div>
        </div>

        {createMutation.error && <Alert type="error">{humaniseErrors(createMutation.error as Error).title}</Alert>}

        <Select
          label={t('devices.table.deviceType')}
          options={deviceTypeOptions}
          value={deviceType}
          onChange={onDeviceTypeChange}
        />
        <Spacer size={16} />

        {showSubType && (
          <>
            <Panel.Subtitle>{t('device.form.subtype')}</Panel.Subtitle>
            <Select value={subType} onChange={setSubType} options={buttonOptions} />
            <Spacer size={16} />
          </>
        )}

        {mode === 'single' ? (
          <>
            {showMac && (
              <>
                <ScannableInput
                  label={t('devices.table.macAddress')}
                  autoCapitalize="characters"
                  value={macAddress}
                  onValueChange={setMacAddress}
                  onScan={onScan}
                  validateScan={validateScan}
                  scanHint={t('deviceScan.hintQr')}
                  error={macError ?? getFieldError('macAddress', createMutation.error as Error)}
                />
                <Spacer size={16} />
              </>
            )}
            {showImeiPhone && (
              <>
                <ScannableInput
                  label={t('devices.table.imeiNumber')}
                  value={imeiNumber}
                  onValueChange={setImeiNumber}
                  onScan={onScan}
                  validateScan={validateScan}
                  scanIcon="barcode"
                  scanHint={t('deviceScan.hintBarcode')}
                  error={imeiError ?? getFieldError('imeiNumber', createMutation.error as Error)}
                />
                <Spacer size={16} />
                <Input
                  label={t('global.phoneNumber')}
                  value={phoneNumber}
                  onValueChange={setPhoneNumber}
                  error={getFieldError('phoneNumber', createMutation.error as Error)}
                />
                <Spacer size={16} />
              </>
            )}
          </>
        ) : (
          <>
            <Panel.Subtitle>
              {t('device.form.csvHint', { columns: requiredCsvColumns(deviceType).join(', ') })}
            </Panel.Subtitle>
            <FileDrop
              label={t('device.form.csvUpload')}
              onChange={handleFile}
              onInvalid={() => undefined}
              isFileValid={isFileValid}
              setIsFileValid={setIsFileValid}
              allowedFileTypes={CSV_FILE_TYPES}
              value={csvFile}
              clearText={t('global.cancel')}
            />
            {csvFile && !isFileValid && (
              <>
                <Spacer size={16} />
                <Alert type="error">{t('device.form.csvInvalid')}</Alert>
              </>
            )}
            {parsed.errors.length > 0 && (
              <>
                <Spacer size={16} />
                <Alert type="error">
                  {parsed.errors.map((error, index) => (
                    <p key={index} className="mb-0">
                      {error}
                    </p>
                  ))}
                </Alert>
              </>
            )}
            {parsed.rows.length > 0 && parsed.errors.length === 0 && (
              <p className="text-muted mt-10 mb-0">{t('device.form.csvParsed', { count: parsed.rows.length })}</p>
            )}
            {failedResults.length > 0 && (
              <>
                <Spacer size={16} />
                <Alert type="error">
                  <p className="mb-5">
                    {t('device.form.batchResults', { succeeded: succeededCount, failed: failedResults.length })}
                  </p>
                  {failedResults.map(({ result, row }, index) => (
                    <p key={index} className="mb-0">
                      {t('device.form.batchRowError', {
                        device: row.macAddress || row.imeiNumber || '-',
                        error: humaniseErrors(result.reason as Error).title,
                      })}
                    </p>
                  ))}
                </Alert>
              </>
            )}
            <Spacer size={16} />
          </>
        )}

        <Input
          label={t('device.form.description')}
          value={description}
          onValueChange={setDescription}
          error={getFieldError('description', createMutation.error as Error)}
        />
        <Spacer size={32} />

        {mode === 'batch' ? (
          <>
            <Button
              className="mb-15"
              disabled={createMutation.isPending || !canSubmit}
              loading={createMutation.isPending && createMutation.variables === false}
              isBlock
              buttonType="success"
              type="button"
              onClick={() => createMutation.mutate(false)}
            >
              {t('device.form.batchConfirm', { count: parsed.rows.length })}
            </Button>
            {!isInstaller && (
              <Button
                className="mb-15"
                disabled={createMutation.isPending || !canSubmit}
                loading={createMutation.isPending && createMutation.variables === true}
                isBlock
                buttonType="inverse"
                type="button"
                onClick={() => createMutation.mutate(true)}
              >
                {t('device.form.batchConfirmArchive', { count: parsed.rows.length })}
              </Button>
            )}
          </>
        ) : (
          <Button
            className="mb-15"
            disabled={createMutation.isPending || !canSubmit}
            loading={createMutation.isPending}
            isBlock
            buttonType="success"
            type="button"
            onClick={() => createMutation.mutate(false)}
          >
            {t('device.form.confirm')}
          </Button>
        )}
        <Button onClick={() => onClose()} disabled={createMutation.isPending} isBlock buttonType="link">
          {t('global.cancel')}
        </Button>
      </>
    </Modal>
  );
}
